import { useMemo, useState, useCallback } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { useRouter } from '@/routes/hooks/use-router';
import { useAuthContext } from '@/auth/hooks';
import { paths } from '@/routes/paths';
import { OrionAccess, OrionModule, OrionSubmodule } from '@/types';
import axios from '@/utils/api/axios';

interface UseOrderTabsProps {
  initialTab?: string;
}

export function useOrderTabs({ initialTab }: UseOrderTabsProps = {}) {
  const { t } = useTranslation();
  const router = useRouter();
  const { user } = useAuthContext();

  const [currentTab, setCurrentTab] = useState(initialTab ?? 'purchase');

  const canViewOrders = user?.checkPermission(
    OrionModule.ACCOUNT,
    OrionSubmodule.ACCOUNT.ORDER,
    OrionAccess.VIEW
  );
  const canViewBilling = user?.checkPermission(
    OrionModule.ACCOUNT,
    OrionSubmodule.ACCOUNT.BILLING_ORDER,
    OrionAccess.VIEW
  );
  const canViewRequests = user?.checkPermission(
    OrionModule.ACCOUNT,
    OrionSubmodule.ACCOUNT.REQUEST,
    OrionAccess.VIEW
  );

  const fetchPendingApproval = async () => {
    const response = await axios.post('/users/approval/pending');
    return response.data;
  };

  const { data: pendingData } = useQuery(['user-request-pending-total'], fetchPendingApproval, {
    keepPreviousData: true,
    refetchOnMount: 'always',
    enabled: !!canViewRequests,
  });

  const TABS = useMemo(() => {
    const tabs: Array<{ value: string; label: string; path: string; badgeContent?: number }> = [];

    if (canViewOrders) {
      tabs.push({
        value: 'purchase',
        label: t('Purchase'),
        path: paths.management.accounts.orders.root,
      });
    }

    if (canViewBilling) {
      tabs.push({
        value: 'billing',
        label: t('Billing'),
        path: paths.management.accounts.billingOrders.root,
      });
    }

    if (canViewRequests) {
      tabs.push({
        value: 'requests',
        label: t('Requests'),
        path: paths.management.accounts.request.root,
      });
      tabs.push({
        value: 'access',
        label: t('Access'),
        path: paths.management.accounts.request.users.root,
        badgeContent: pendingData?.total ?? 0,
      });
    }

    return tabs;
  }, [canViewOrders, canViewBilling, canViewRequests, pendingData?.total, t]);

  const handleChangeTab = useCallback(
    (event: React.SyntheticEvent, newValue: string) => {
      const nextTab = TABS.find((item) => item.value === newValue);
      if (!nextTab) return;
      router.push(nextTab.path);
      setCurrentTab(newValue);
    },
    [TABS, router]
  );

  return { currentTab, TABS, handleChangeTab };
}
